import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CasesRepository } from '../../../infrastructure/repositories/cases.repository';
import { DisplayFieldValueEntity } from '../../../domain/entities/display-field-value.entity';
import type { GetCaseForPrintQuery } from './get-case-for-print.query';
import type { GetCaseForPrintResult } from './get-case-for-print.result';

/**
 * Get case for print use case.
 * Fetches optimized print data in a single API call.
 * Includes automatic audit logging and PII redaction on backend.
 *
 * @usageNotes
 * This use case does NOT update state - it's read-only for printing.
 * Backend automatically logs "Viewed [Record ID]" audit event.
 * Backend automatically redacts PII based on user permissions.
 */
@Injectable({ providedIn: 'root' })
export class GetCaseForPrintUseCase {
  private readonly _repository = inject(CasesRepository);

  public execute(query: GetCaseForPrintQuery): Observable<GetCaseForPrintResult> {
    return this._repository.getCaseForPrint(query.caseId).pipe(
      map((result) => {
        // Convert CaseFieldValueDisplayEntity[] to DisplayFieldValueEntity[] for print
        const displayValues = result.fieldValues
          .filter(fv => fv.value != null && fv.value !== '')
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(fv => {
            const entity = new DisplayFieldValueEntity();
            entity.label = fv.fieldLabel;
            entity.value = this._formatFieldValue(fv.value, fv.fieldType);
            return entity;
          });

        return {
          case: result.case,
          fieldValues: displayValues,
          involvedEmployees: result.involvedEmployees,
        };
      }),
      catchError((error) => {
        // Preserve HttpErrorResponse (including status for 403/404 handling)
        return throwError(() => error);
      })
    );
  }

  /**
   * Formats a field value for display in print mode.
   * Parses JSON strings and handles different data types based on fieldType.
   */
  private _formatFieldValue(value: string | null | undefined, fieldType: string): string {
    if (!value) {
      return 'N/A';
    }

    try {
      const parsed = JSON.parse(value);

      // Date fields - format as readable date
      if (fieldType === 'date' && typeof parsed === 'string') {
        const dateValue = new Date(parsed);
        if (!isNaN(dateValue.getTime())) {
          return dateValue.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          });
        }
      }

      // Time fields - format as readable time
      if (fieldType === 'time' && typeof parsed === 'string') {
        const timeValue = new Date(parsed);
        if (!isNaN(timeValue.getTime())) {
          return timeValue.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          });
        }
      }

      // Currency/number fields - format with appropriate precision
      if ((fieldType === 'currency' || fieldType === 'number') && typeof parsed === 'number') {
        if (fieldType === 'currency') {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
          }).format(parsed);
        }
        return String(parsed);
      }

      // Array fields (checkbox, multi-select)
      if (Array.isArray(parsed)) {
        return parsed.join(', ');
      }

      // Boolean fields
      if (typeof parsed === 'boolean') {
        return parsed ? 'Yes' : 'No';
      }

      // Object fields - stringify
      if (typeof parsed === 'object' && parsed !== null) {
        return JSON.stringify(parsed);
      }

      // Default: convert to string
      return String(parsed);
    } catch {
      // If not valid JSON, return as-is
      return value;
    }
  }
}
